
interface ModelGalleryProps {
  images: string[];
  selectedImage: number;
  onImageSelect: (index: number) => void;
  modelName: string;
}

export default function ModelGallery({ images, selectedImage, onImageSelect, modelName }: ModelGalleryProps) {
  const handleThumbnailClick = (index: number) => {
    onImageSelect(index);
  };

  const handleZoomClick = () => {
    // Create a modal overlay for zoomed image
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 cursor-pointer';
    modal.onclick = () => document.body.removeChild(modal);
    
    const img = document.createElement('img');
    img.src = images[selectedImage];
    img.className = 'max-w-[90vw] max-h-[90vh] object-contain';
    img.alt = `${modelName} - Zoomed View`;
    
    modal.appendChild(img);
    document.body.appendChild(modal);
  };

  const handleShareClick = async () => {
    const shareData = {
      title: modelName,
      text: `Check out this amazing 3D model: ${modelName}`,
      url: window.location.href
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback: copy URL to clipboard
        await navigator.clipboard.writeText(window.location.href);
        // You could show a toast notification here
        console.log('URL copied to clipboard');
      }
    } catch (err) {
      console.error('Error sharing:', err);
    }
  };

  const handleFavoriteClick = () => {
    // Toggle favorite status (you could integrate with local storage or backend)
    const favorites = JSON.parse(localStorage.getItem('favorites') || '[]');
    const modelId = window.location.pathname.split('/').pop();
    
    if (favorites.includes(modelId)) {
      const updatedFavorites = favorites.filter((id: string) => id !== modelId);
      localStorage.setItem('favorites', JSON.stringify(updatedFavorites));
    } else {
      favorites.push(modelId);
      localStorage.setItem('favorites', JSON.stringify(favorites));
    }
  };

  return (
    <div className="space-y-4">
      {/* Main Image */}
      <div className="aspect-square bg-gray-50 rounded-xl overflow-hidden">
        <img
          src={images[selectedImage]}
          alt={`${modelName} - View ${selectedImage + 1}`}
          className="w-full h-full object-cover cursor-zoom-in"
          onClick={handleZoomClick}
        />
      </div>

      {/* Thumbnail Gallery */}
      <div className="grid grid-cols-4 gap-4">
        {images.map((image, index) => (
          <button
            key={index}
            onClick={() => handleThumbnailClick(index)}
            className={`aspect-square rounded-lg overflow-hidden border-2 transition-all duration-200 cursor-pointer hover:scale-105 ${
              selectedImage === index 
                ? 'border-blue-600 shadow-lg' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <img
              src={image}
              alt={`${modelName} - Thumbnail ${index + 1}`}
              className="w-full h-full object-cover"
            />
          </button>
        ))}
      </div>

      {/* Image Controls */}
      <div className="flex justify-center gap-4">
        <button 
          onClick={handleZoomClick}
          className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors cursor-pointer"
          title="Zoom Image"
        >
          <i className="ri-zoom-in-line text-lg"></i>
        </button>
        <button 
          onClick={handleShareClick}
          className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors cursor-pointer"
          title="Share Model"
        >
          <i className="ri-share-line text-lg"></i>
        </button>
        <button 
          onClick={handleFavoriteClick}
          className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors cursor-pointer"
          title="Add to Favorites"
        >
          <i className="ri-heart-line text-lg"></i>
        </button>
      </div>
    </div>
  );
}
